import { UI, FONT, T } from "../constants/tokens";
import { closePrivacy } from "../lib/privacyRoute";
import Icon from "./Icon";
import LTR from "./LTR";

// ─── Privacy policy ──────────────────────────────────────────────────────────
// A full page rather than a modal: the document is long and carries a table,
// and a sheet would put both inside a scroll box the reader has to fight.
// Rendered instead of the app, reachable at #/privacy with or without a
// session — see src/lib/privacyRoute.js for why the route is a hash.
//
// The wording is fixed. It was written and approved as a whole, so nothing
// here reformats, shortens or reorders it; the components below only decide
// type, spacing and direction. Audited line by line against the code on
// 2026-09-17 (Finnhub proxy, full provider list, guest accounts, browser
// storage) and edited with approval.
//
// Colour: none. No accent anywhere on the page — gold is reserved for insight,
// Pro and the one primary action, and a legal document is none of the three.
// The whole document reads on the neutral text ramp, and the only structural
// colour is a hairline.

// The date the document itself last changed — NOT the app version and NOT the
// build date. Update it only when the text below is edited; a date that moves
// on every release tells the reader nothing about the policy they agreed to.
const UPDATED = "18.09.2026";

const MAIL = "getsuricata@gmail.com";

// Latin runs inside Hebrew prose — service names, protocols, initialisms —
// get bidi-isolated so the punctuation around them lands on the correct side.
// Done by pattern rather than by hand: the document names a dozen services and
// a missed one is a visible glitch, not a silent one.
const LATIN_RUN = /[A-Za-z][A-Za-z0-9._-]*(?: [A-Za-z][A-Za-z0-9._-]*)*/g;

function bidi(text) {
  const parts = [];
  let last = 0;
  let m;
  LATIN_RUN.lastIndex = 0;
  while ((m = LATIN_RUN.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<LTR key={m.index} style={{ fontFamily: FONT.latin }}>{m[0]}</LTR>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

const BODY = { fontSize: 15.5, lineHeight: 2, color: UI.text.secondary, margin: "0 0 12px" };

function P({ children }) {
  return <p style={BODY}>{typeof children === "string" ? bidi(children) : children}</p>;
}

// A bold run that labels the block under it — "פרטי חשבון", "מה נשלח".
// Primary text, so it separates from the body by weight and brightness rather
// than by a colour the page does not otherwise use.
function Sub({ children }) {
  return (
    <div style={{ fontSize: 15, fontWeight: 800, color: UI.text.primary, margin: "22px 0 8px", lineHeight: 1.7 }}>
      {bidi(children)}
    </div>
  );
}

function List({ items }) {
  return (
    <ul style={{ ...BODY, paddingInlineStart: 22, margin: "0 0 12px" }}>
      {items.map((it, i) => (
        <li key={i} style={{ marginBottom: 4 }}>{bidi(it)}</li>
      ))}
    </ul>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginTop: 34, paddingTop: 26, borderTop: `1px solid ${UI.line.hairline}` }}>
      <h2 style={{
        fontFamily: FONT.heading, fontSize: 20, fontWeight: 800,
        color: UI.text.primary, margin: "0 0 12px", lineHeight: 1.5,
      }}>
        {bidi(title)}
      </h2>
      {children}
    </section>
  );
}

function Mail() {
  return (
    <LTR>
      <a
        href={`mailto:${MAIL}`}
        style={{ color: UI.text.primary, fontFamily: FONT.latin, textDecoration: "underline", textUnderlineOffset: 3 }}>
        {MAIL}
      </a>
    </LTR>
  );
}

// Two columns, so it fits a narrow screen as-is: no horizontal scroll box and
// no card-per-row fallback. The cells wrap, which is what a two-column table
// is for.
const STORED_LOCALLY = [
  ["אסימון הכניסה", "כדי שלא תצטרך להתחבר בכל פעם"],
  ["שפת הממשק וערכת הצבעים", "העדפות תצוגה"],
  ["התקדמות באקדמיה ותוצאת המבחן", "סימון שיעורים שהושלמו"],
  ["עותק של תשובות השאלון", "טעינה מהירה"],
  ["כתובת תמונת הפרופיל", "טעינה מהירה"],
  ["מונה יומי של בקשות סיווג חדשות", "הגבלת עומס · מספר בלבד"],
];

function StorageTable() {
  const cell = {
    padding: "12px 14px", fontSize: 14.5, lineHeight: 1.7,
    textAlign: "right", verticalAlign: "top",
    borderTop: `1px solid ${UI.line.hairline}`,
  };
  return (
    <div style={{
      background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`,
      borderRadius: T.radius.md, overflow: "hidden", margin: "0 0 14px",
    }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: UI.surface.sunken }}>
            <th style={{ ...cell, borderTop: "none", width: "45%", fontWeight: 800, color: UI.text.primary }}>מה</th>
            <th style={{ ...cell, borderTop: "none", fontWeight: 800, color: UI.text.primary }}>לשם מה</th>
          </tr>
        </thead>
        <tbody>
          {STORED_LOCALLY.map(([what, why]) => (
            <tr key={what}>
              <td style={{ ...cell, color: UI.text.primary, fontWeight: 600 }}>{what}</td>
              <td style={{ ...cell, color: UI.text.secondary }}>{why}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PrivacyPolicy() {
  return (
    <div dir="rtl" style={{
      minHeight: "100dvh", background: UI.surface.page,
      color: UI.text.primary, fontFamily: FONT.body,
    }}>
      {/* Back bar. Sticky, because the document is long and the way out should
          not depend on scrolling to either end of it. */}
      <div style={{
        position: "sticky", top: 0, zIndex: 5,
        background: UI.surface.page, borderBottom: `1px solid ${UI.line.hairline}`,
        padding: "12px 22px",
      }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <button
            onClick={closePrivacy}
            style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              minHeight: 40, padding: "8px 14px 8px 12px",
              background: "transparent", border: `1px solid ${UI.line.hairline}`,
              borderRadius: T.radius.sm, cursor: "pointer",
              color: UI.text.secondary, fontSize: 14.5, fontWeight: 700, fontFamily: "inherit",
            }}>
            {/* Right-pointing in RTL: back is the direction the text comes from */}
            <Icon name="chevronRight" size={16} color={UI.text.secondary}/>
            חזרה
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "26px 22px 96px" }}>
        <h1 style={{
          fontFamily: FONT.heading, fontSize: "clamp(24px, 5.5vw, 30px)", fontWeight: 800,
          color: UI.text.primary, margin: "0 0 10px", lineHeight: 1.45,
        }}>
          {bidi("מדיניות פרטיות · Suricata")}
        </h1>

        <div style={{ fontSize: 14, color: UI.text.muted, lineHeight: 1.8, marginBottom: 4 }}>
          <strong style={{ color: UI.text.secondary, fontWeight: 800 }}>עדכון אחרון:</strong>{" "}
          <LTR style={{ fontFamily: FONT.latin }}>{UPDATED}</LTR>
        </div>

        <Section title="מי אנחנו">
          <P>Suricata היא אפליקציית מידע ומעקב אחר תיק השקעות, המופעלת על ידי דוד נגר.</P>
          <P>
            <strong style={{ color: UI.text.primary, fontWeight: 800 }}>יצירת קשר:</strong>{" "}
            <Mail/>
          </P>
          <P>Suricata אינה חברת השקעות ואינה מספקת ייעוץ. מפעיל השירות אינו יועץ השקעות מורשה. כל המידע באפליקציה הוא מידע בלבד, ואינו המלצה או קריאה לפעולה.</P>
        </Section>

        <Section title="מה נאסף">
          <Sub>פרטי חשבון</Sub>
          <List items={[
            "כתובת אימייל",
            "סיסמה · נשמרת באמצעות מנגנון hashing. המפעיל אינו יכול לראות אותה",
            "שם, אם מסרת אותו בשאלון ההיכרות",
            "תאריך ההרשמה ותאריך הכניסה האחרונה",
          ]}/>

          <Sub>מידע שאתה מזין</Sub>
          <List items={[
            "החזקות בתיק · סימול, כמות, מחיר קנייה, תאריך קנייה",
            "התראות מחיר · סימול, כיוון, מחיר יעד",
            "חשבון הסימולציה · מזומן, עסקאות, רווח והפסד",
            "תשובות שאלון פרופיל המשקיע · מטרת ההשקעה, אופק הזמן, התגובה לירידה בשוק, חלק החיסכון המושקע בשוק ההון, וזמן המעקב הרצוי",
            "תמונת פרופיל, אם העלית",
            "סימון תחום פעילות להחזקה · הבחירה שסימנת מתוך פילוח החברה, והאחוז בזמן הסימון",
          ]}/>

          <Sub>מדידת שימוש</Sub>
          <List items={[
            "פתיחת מסכים · שם המסך וזמן הפתיחה, נשמרים 90 יום",
          ]}/>

          <Sub>מצב אורח</Sub>
          <P>כניסה כאורח יוצרת חשבון אנונימי · ללא אימייל, שם או סיסמה. תיק ההתחלה, חשבון הסימולציה וההעדפות נשמרים תחת החשבון הזה, והגישה אליו נשענת על אסימון בדפדפן בלבד. ניקוי נתוני הדפדפן מנתק את הגישה, והחשבון האנונימי ונתוניו נשארים במסד הנתונים ללא דרך לשייך אותם אליך.</P>

          <Sub>כתובת IP ומידע טכני</Sub>
          <P>Suricata אינה שומרת כתובות IP או נתוני מכשיר במסד הנתונים שלה. ספק האירוח, Netlify, מתעד כל בקשה לשרתי השירות עם כתובת ה-IP שלה, לצורכי תפעול ואבטחה, לזמן מוגבל ובהתאם למדיניות שלו.</P>
          <P>עם זאת, כאשר האפליקציה פונה לשירותים של צדדים שלישיים · ישירות מהדפדפן שלך או משרתי השירות · אותם ספקים עשויים לקבל כתובת IP ומידע טכני, בהתאם למדיניות הפרטיות שלהם. פירוט בסעיף "למי המידע מועבר".</P>
          <P>אין באפליקציה ובאתר כלי מדידה של צד שלישי · לא Google Analytics, לא פיקסלים פרסומיים, ולא כלי ניתוח התנהגות. המדידה היחידה היא רישום פתיחת מסכים במסד הנתונים של Suricata, כמפורט למעלה.</P>
        </Section>

        <Section title="למה המידע נאסף">
          <P>להפעלת השירות בלבד: הצגת התיק, חישוב שווי, שליחת התראות, והתאמת אופן הצגת המידע.</P>
          <P>המידע אינו נמכר, אינו מושכר, ואינו משמש לפרסום.</P>

          <Sub>בסיס העיבוד</Sub>
          <P>המידע מעובד ככל הנדרש לצורך אספקת השירות, ניהול החשבון, אבטחת השירות, עמידה בחובות משפטיות החלות על המפעיל, ובמקרים שבהם נדרש · על בסיס הסכמת המשתמש.</P>

          <Sub>פרופיל המשקיע</Sub>
          <P>תשובות השאלון משמשות להתאמת אופן הצגת המידע באפליקציה בלבד.</P>
          <P>הן אינן משמשות לקבלת החלטות השקעה עבורך, אינן מייצרות המלצות, ואינן מהוות סיווג לקוח לצורך ייעוץ. Suricata מציגה עובדות עם מקור, ואינה מכוונת לפעולה.</P>
        </Section>

        <Section title="שימוש בבינה מלאכותית">
          <P>האפליקציה משתמשת במודל שפה של Google (Gemini) לעיבוד טקסט.</P>

          <Sub>מה נשלח</Sub>
          <List items={[
            "כותרות ותקצירים של כתבות חדשות · תוכן ציבורי ממקורות חדשותיים",
            "כותרות ותקצירים של מסמכים רשמיים מה-Federal Register · פרסומים ציבוריים של הממשל האמריקאי",
            "סימולי מניות מלוח הדוחות הקרובים · כאשר מוצג לוח הדוחות, הסימולים הרלוונטיים נשלחים לצורך ניסוח תמצית",
            "הסעיף העסקי מהדוח השנתי של חברות · טקסט ציבורי מדיווחי SEC, לצורך תרגום לעברית",
          ]}/>

          <Sub>מה אינו נשלח</Sub>
          <List items={[
            "כתובת האימייל שלך",
            "מזהה החשבון שלך",
            "שמך",
            "רשימת ההחזקות שלך כמכלול",
            "תשובות השאלון",
          ]}/>

          <P>הבקשות נשלחות משרתי Suricata, ולא ישירות מהדפדפן שלך.</P>
          <P>Suricata אינה משתמשת במידע לצורך אימון מודלים, ואינה שומרת היסטוריית עיבוד. השימוש שעושה ספק המודל במידע כפוף לתנאיו ולמדיניות הפרטיות שלו.</P>
          <P>אם בעתיד יישלח מידע נוסף לעיבוד AI, מדיניות זו תעודכן והשינוי יוצג באפליקציה.</P>
        </Section>

        <Section title="למי המידע מועבר">
          <Sub>Supabase · אחסון ואימות</Sub>
          <P>מאחסן את כל נתוני החשבון והתיק. השרתים ממוקמים בפרנקפורט, גרמניה · בתוך האיחוד האירופי.</P>

          <Sub>Netlify · הרצת השירות</Sub>
          <P>מארח את האפליקציה ואת פונקציות השרת. יומני הבקשות של Netlify כוללים כתובת IP, מדינה ואזור · ראה "כתובת IP ומידע טכני".</P>

          <Sub>Finnhub · נתוני שוק</Sub>
          <P>מחירים, פרופילי חברות, חדשות ולוח הדוחות מגיעים מ-Finnhub. הבקשות נשלחות משרתי Suricata ולא מהדפדפן שלך · Finnhub מקבלת את הסימולים המבוקשים ואת כתובת השרת, ולא את כתובת ה-IP שלך.</P>
          <P>הסימולים נשלחים ללא זיהוי: Finnhub אינה מקבלת את שמך, את כתובת האימייל שלך או את מזהה החשבון שלך, ואינה יכולה לקשר בין בקשות של משתמשים שונים.</P>

          <Sub>מקורות נתונים נוספים · דרך השרת</Sub>
          <P>FRED (הבנק הפדרלי של סנט לואיס · מדדים ומאקרו), SEC EDGAR (דיווחי חברות), בנק ישראל (ריבית) ו-Yahoo Finance (שער USD/ILS) נקראים משרתי Suricata בלבד. הבקשות אינן כוללות מידע עליך, וספקים אלה אינם מקבלים את כתובת ה-IP שלך.</P>

          <Sub>ExchangeRate-API · מהדפדפן</Sub>
          <P>שערי המטבע ל-EUR ול-GBP, וגיבוי לשער USD/ILS, נטענים ישירות מהדפדפן שלך מ-open.er-api.com. הבקשה אינה כוללת סימולים או מידע על התיק, אך כתובת ה-IP שלך נחשפת לספק.</P>

          <Sub>Google</Sub>
          <P>בקשות ל-Gemini נשלחות משרתי Suricata ולא מהדפדפן שלך. אף שירות של Google אינו נטען ישירות מהדפדפן.</P>
          <P>הגופנים מוגשים משרתי Suricata עצמם. Google Fonts אינו נטען, ואינו מקבל דבר.</P>

          <Sub>Resend</Sub>
          <P>מספק את שירות שליחת המיילים של המערכת · אימות הרשמה ואיפוס סיסמה. כתובת האימייל שלך עוברת דרכו.</P>

          <Sub>TradingView</Sub>
          <P>גרפי המניות נטענים מ-TradingView. הרכיב מקבל את כתובת ה-IP שלך ואת הסימול שאתה צופה בו.</P>

          <Sub>ספקי התראות דחיפה</Sub>
          <P>אם הפעלת התראות, מנוי הדחיפה נשלח דרך שירות הדחיפה של הדפדפן שלך · Google, Apple או Mozilla, בהתאם לדפדפן. תוכן ההתראה מוצפן, אך מועד השליחה והמכשיר גלויים לספק.</P>
        </Section>

        <Section title="עיבוד מידע מחוץ לישראל">
          <P>נתוני החשבון והתיק מאוחסנים בשרתים באיחוד האירופי.</P>
          <P>מידע עשוי להיות מעובד מחוץ לישראל ולתחומי האיחוד האירופי, בהתאם למקום פעילותם של ספקי השירות ולמנגנוני העברת המידע החלים עליהם לפי הדין הרלוונטי.</P>
        </Section>

        <Section title="תמונת הפרופיל">
          <P>תמונת הפרופיל נשמרת באחסון ציבורי. מי שמחזיק בכתובת הקובץ יכול לצפות בה ללא התחברות. הכתובת אינה מפורסמת בשום מקום, אך היא ניתנת לשחזור ממזהה החשבון.</P>
          <P>התמונה נמחקת עם מחיקת החשבון.</P>
        </Section>

        <Section title="מה נשמר בדפדפן שלך">
          <P>האפליקציה שומרת מידע מקומי במכשיר שלך:</P>
          <StorageTable/>
          <P>אין באפליקציה עוגיות מעקב. המידע נמחק בעת מחיקת החשבון או ניקוי נתוני הדפדפן.</P>
        </Section>

        <Section title="כמה זמן המידע נשמר">
          <P>המידע נשמר כל עוד החשבון פעיל.</P>
          <P>בעת בקשת מחיקה: החשבון מושבת מיד והגישה לאפליקציה נחסמת. הנתונים נשמרים עוד 30 יום, ובמהלכם ניתן לשחזר את החשבון.</P>
          <P>בתום תקופת השחזור, המידע נמחק ממערכות ההפעלה של Suricata. עותקי גיבוי אצל ספקי התשתית עשויים להימחק במועד מאוחר יותר, בהתאם למחזורי הגיבוי שלהם.</P>
        </Section>

        <Section title="מידע ניהולי">
          <P>לצורך תמיכה ותפעול, המפעיל עשוי לשמור הערות ותיוגים הקשורים לחשבונך. מידע זה נמחק עם מחיקת החשבון.</P>
          <P>בנוסף נשמר יומן פעולות ניהוליות · מתי התבקשה מחיקה ומתי בוצעה. היומן שומר את מזהה החשבון בלבד, ללא כתובת אימייל, ונשמר גם לאחר המחיקה.</P>
        </Section>

        <Section title="הזכויות שלך">
          <List items={[
            "לעיין במידע השמור עליך",
            "לתקן מידע שגוי · דרך מסך ההגדרות",
            "למחוק את החשבון · דרך מסך ההגדרות",
            "לייצא את ההחזקות שלך לקובץ CSV",
          ]}/>
          <P>הייצוא הקיים כולל את ההחזקות בלבד. הוא אינו כולל התראות מחיר, את חשבון הסימולציה או את תשובות השאלון.</P>
          <P>הזכויות כפופות לדין החל.</P>
          <P><>לכל בקשה: <Mail/></></P>
        </Section>

        <Section title="אבטחה">
          <List items={[
            "הסיסמאות נשמרות באמצעות hashing. אין למפעיל גישה אליהן",
            "הגישה לנתונים מוגבלת ברמת מסד הנתונים · כל משתמש רואה את הנתונים שלו בלבד",
            "כל התקשורת מוצפנת ב-HTTPS",
          ]}/>
          <P>עם זאת, אין מערכת מאובטחת לחלוטין, ואיננו יכולים להבטיח הגנה מוחלטת מפני גישה בלתי מורשית.</P>
        </Section>

        <Section title="קטינים">
          <P>השירות אינו מיועד למי שטרם מלאו לו 18. איננו אוספים ביודעין מידע מקטינים.</P>
        </Section>

        <Section title="שינויים במדיניות">
          <P>מדיניות זו עשויה להתעדכן. עדכון מהותי יוצג באפליקציה. תאריך העדכון האחרון מופיע בראש העמוד.</P>
        </Section>

        <Section title="יצירת קשר">
          <P><Mail/></P>
        </Section>

        <div style={{
          marginTop: 36, paddingTop: 22, borderTop: `1px solid ${UI.line.hairline}`,
          fontSize: 13, color: UI.text.muted, fontStyle: "italic", textAlign: "center",
        }}>
          מידע בלבד · אינו ייעוץ השקעות
        </div>
      </div>
    </div>
  );
}
